export const ABOUT_BACH = {
  eyebrow: "About Bach Flower",
  pullquote: "Nature has a solution for everything.",
  intro:
    "At Bloom & Heal, we work with a simple truth — lasting well-being starts on the inside. Bach Flower Remedies open a quiet, natural doorway to that inner calm, one blossom and one feeling at a time. Drawn from 38 wildflowers and prepared with nothing more than pure spring water and sunlight, each essence gently answers the emotion beneath a symptom — safe for every age, and easy to weave into daily life through a personalised, judgement-free session.",
};

export const ACCORDIONS = [
  {
    id: "why-emotions",
    title: "Why emotions matter ?",
    body: [
      "Emotions are natural. Yet when they become intense, suppressed, prolonged or imbalanced, they quietly affect daily functioning, relationships and mental peace.",
      "Very often, this emotional imbalance shows up in the body as physical symptoms — headaches, fatigue, disturbed sleep, digestive trouble, tension, unexplained aches, stammering, or shaky hands and legs — the body's way of expressing what the mind has been holding.",
      "Unresolved emotional challenges over time can ripple into physical well-being. Honouring our emotional landscape is the first gentle step toward wholeness.",
    ],
  },
  {
    id: "emotional-states",
    title: "Emotional states supported",
    list: [
      "Fear, anxiety, stress, panic",
      "Overwhelm and emotional exhaustion",
      "Lack of trust, self-doubt",
      "Uncertainty, confusion, indecision",
      "Sadness, grief, despair",
      "Anger, irritability, impatience, frustration",
      "Loneliness or feeling emotionally disconnected",
      "Lack of confidence and motivation",
      "Overthinking and mental restlessness",
      "Sensitivity to the views of others",
      "Emotional shock, trauma, unresolved past hurts",
      "Over-concern for loved ones",
    ],
  },
  {
    id: "personality-patterns",
    title: "Personality & behavioural patterns supported",
    list: [
      "Rigidity, perfectionism",
      "Critical, judgmental, intolerant tendencies",
      "Dominating or overly authoritative",
      "People-pleasing — unable to say “no”",
      "Possessiveness, jealousy, emotional dependency",
      "Burning out from excessive enthusiasm",
      "Difficulty adapting to change",
      "Stuck in repetitive patterns",
    ],
  },
  {
    id: "why-choose",
    title: "Why choose Bach Flower Remedies ?",
    list: [
      "A natural wellness approach",
      "Gentle, with no known side effects",
      "Non-invasive emotional support",
      "Addresses underlying emotional patterns, not just symptoms",
      "Suitable for those sensitive to medications",
      "For all ages — children, adults, seniors, even pets",
      "Can be used alongside other treatments",
      "Easy to consume and integrate into daily life",
    ],
  },
  {
    id: "how-it-works",
    title: "How it works ?",
    body: [
      "Following a personal consultation, a remedy bottle is prepared with a combination of up to seven essences chosen specifically for your emotional landscape.",
      "Drops are taken orally — directly on the tongue or diluted in water. Duration and frequency adapt to your unique patterns and situation.",
    ],
  },
  {
    id: "is-it-for-you",
    title: "Is it for you ?",
    list: [
      "Do you smile while worries and tears stay hidden ?",
      "Do you fear something — known or unknown ?",
      "Do you feel intolerant or over-critical ?",
      "Do you lack self-esteem or faith in yourself ?",
      "Are you stuck in a loop of repeating life patterns ?",
      "Are you possessive or in need of constant attention ?",
      "Do you feel dreamy and absent-minded ?",
      "Do you feel overwhelmed by responsibility ?",
      "Do you feel ‘Monday blues’, mental tiredness or procrastinate ?",
      "Are you impatient and restless ?",
      "Are you a workaholic — duty-driven to burnout ?",
      "Do you feel unworthy, sorry or guilty ?",
      "Are you overly self-disciplined or rigid ?",
      "Are you indecisive or confused ?",
      "Are you stuck in shock or trauma ?",
      "Are you a perfectionist or over-enthusiastic ?",
      "Are you bothered by what people say around you ?",
      "Do you feel aloof, lonely or lost ?",
      "Do you struggle in a new phase of life ?",
    ],
  },
];

export const SEGMENTS = [
  {
    id: "children",
    title: "Children & Teenagers",
    desc: "Gentle support through fear, anxiety, social shyness, school stress and the natural emotional ups and downs of growing up.",
  },
  {
    id: "relationships",
    title: "Relationships & Recovery",
    desc: "Care during conflicts, breakups, divorce, family strain, loss, grief and unresolved trauma.",
  },
  {
    id: "work",
    title: "Work & Professional Life",
    desc: "Steadiness during pressure, exhaustion, fear of failure, setbacks and the weight of overwhelm.",
  },
  {
    id: "transitions",
    title: "Life Transitions",
    desc: "Companionship through puberty, career change, relocation, marriage, child birth and retirement.",
  },
];

export const SESSIONS = [
  "One-on-one counselling sessions",
  "Online & offline consultations",
  "Personalized Bach Flower Remedy bottles",
  "Ongoing emotional wellness support",
];

// Numbered manifesto chapters — the editorial heart of the page.
export const MANIFESTO = [
  {
    no: "01",
    title: "Emotions are not problems to be fixed",
    body:
      "They are messengers. When honoured rather than suppressed, even the heaviest feelings begin to move. Our work starts by simply listening — to you, and to what your emotions are trying to say.",
  },
  {
    no: "02",
    title: "Nature already holds the remedy",
    body:
      "Dr. Edward Bach's 38 flower essences carry the vibrational quality of the plants they come from — gentle, natural and free of chemicals. Each blend is prepared for your emotional landscape, never off a shelf.",
  },
  {
    no: "03",
    title: "Healing is a rhythm, not a verdict",
    body:
      "Change rarely arrives in a single conversation. It returns quietly, week by week, once you feel truly heard. We walk beside you at your own pace — with counselling, EFT and personalised remedies.",
  },
];

export const MARQUEE = [
  "Bach Flower Remedies",
  "Emotional Balance",
  "EFT",
  "Gentle Healing",
  "Personal Counselling",
];

// The 38 Bach Flower Remedies grouped by Dr. Bach's seven emotional families.
// Each remedy is generated as a photorealistic macro image at /flowers/{id}.jpg (0-37).
export const REMEDY_GROUPS = [
  {
    id: "fear",
    title: "For Fear",
    remedies: [
      { id: "rock-rose", name: "Rock Rose", note: "Terror, panic, sudden fright" },
      { id: "mimulus", name: "Mimulus", note: "Known fears, shyness, timidity" },
      { id: "cherry-plum", name: "Cherry Plum", note: "Fear of losing control" },
      { id: "aspen", name: "Aspen", note: "Vague, unexplained anxiety" },
      { id: "red-chestnut", name: "Red Chestnut", note: "Over-worry for loved ones" },
    ],
  },
  {
    id: "uncertainty",
    title: "For Uncertainty",
    remedies: [
      { id: "cerato", name: "Cerato", note: "Doubting your own judgement" },
      { id: "scleranthus", name: "Scleranthus", note: "Indecision, swinging between two" },
      { id: "gentian", name: "Gentian", note: "Discouragement after a setback" },
      { id: "gorse", name: "Gorse", note: "Hopelessness, giving up" },
      { id: "hornbeam", name: "Hornbeam", note: "Mental tiredness, procrastination" },
      { id: "wild-oat", name: "Wild Oat", note: "Unsure of life's direction" },
    ],
  },
  {
    id: "not-present",
    title: "Not Sufficient Interest in the Present",
    remedies: [
      { id: "clematis", name: "Clematis", note: "Dreamy, absent-minded" },
      { id: "honeysuckle", name: "Honeysuckle", note: "Living in the past" },
      { id: "wild-rose", name: "Wild Rose", note: "Resignation, apathy" },
      { id: "olive", name: "Olive", note: "Complete mental & physical exhaustion" },
      { id: "white-chestnut", name: "White Chestnut", note: "Overthinking, mental chatter" },
      { id: "mustard", name: "Mustard", note: "Sudden gloom without reason" },
      { id: "chestnut-bud", name: "Chestnut Bud", note: "Repeating the same mistakes" },
    ],
  },
  {
    id: "loneliness",
    title: "For Loneliness",
    remedies: [
      { id: "water-violet", name: "Water Violet", note: "Aloof, reserved, self-reliant" },
      { id: "impatiens", name: "Impatiens", note: "Impatient, quick, irritable" },
      { id: "heather", name: "Heather", note: "Self-absorbed, needs company" },
    ],
  },
  {
    id: "oversensitive",
    title: "Oversensitive to Influences",
    remedies: [
      { id: "agrimony", name: "Agrimony", note: "Hides worries behind a smile" },
      { id: "centaury", name: "Centaury", note: "Cannot say no, easily used" },
      { id: "walnut", name: "Walnut", note: "Protection through life changes" },
      { id: "holly", name: "Holly", note: "Jealousy, anger, suspicion" },
    ],
  },
  {
    id: "despondency",
    title: "For Despondency & Despair",
    remedies: [
      { id: "larch", name: "Larch", note: "Lack of self-confidence" },
      { id: "pine", name: "Pine", note: "Guilt, self-blame" },
      { id: "elm", name: "Elm", note: "Overwhelmed by responsibility" },
      { id: "sweet-chestnut", name: "Sweet Chestnut", note: "The dark night of the soul" },
      { id: "star-of-bethlehem", name: "Star of Bethlehem", note: "Shock, grief, trauma" },
      { id: "willow", name: "Willow", note: "Resentment, self-pity" },
      { id: "oak", name: "Oak", note: "Steady struggle without rest" },
      { id: "crab-apple", name: "Crab Apple", note: "Cleansing, self-disgust" },
    ],
  },
  {
    id: "overcare",
    title: "Overcare for Others",
    remedies: [
      { id: "chicory", name: "Chicory", note: "Possessive, needing attention" },
      { id: "vervain", name: "Vervain", note: "Over-enthusiastic, wound-up" },
      { id: "vine", name: "Vine", note: "Dominating, inflexible" },
      { id: "beech", name: "Beech", note: "Critical, intolerant" },
      { id: "rock-water", name: "Rock Water", note: "Rigid self-discipline" },
    ],
  },
];

export const ALL_REMEDIES = REMEDY_GROUPS.flatMap((g) => g.remedies);

export const QUIZ_QUESTIONS = [
  {
    prompt: "Right now, what's most on your mind?",
    options: [
      { text: "A racing worry I can't quite explain", tag: "fear" },
      { text: "Doubt about a decision I need to make", tag: "uncertainty" },
      { text: "Replaying old memories instead of today", tag: "not-present" },
      { text: "Feeling distant from the people around me", tag: "loneliness" },
    ],
  },
  {
    prompt: "When someone else is struggling, you tend to...",
    options: [
      { text: "Take on their pain as if it were your own", tag: "oversensitive" },
      { text: "Feel a wave of hopelessness for them", tag: "despondency" },
      { text: "Try to fix it, then worry you've overstepped", tag: "overcare" },
      { text: "Feel a flicker of that same old fear", tag: "fear" },
    ],
  },
  {
    prompt: "Which line feels closest to true today?",
    options: [
      { text: "I say yes when I really mean no", tag: "oversensitive" },
      { text: "I'm exhausted from carrying too much", tag: "despondency" },
      { text: "I want everyone else to be okay, even at my own cost", tag: "overcare" },
      { text: "I feel unsure which way to turn", tag: "uncertainty" },
    ],
  },
];

export const QUIZ_RESULT_NOTES = {
  fear: "Fear rarely needs fixing — it needs meeting. This remedy helps you feel safe again, gently.",
  uncertainty: "You don't need every answer today. This remedy softens the noise so your own clarity can surface.",
  "not-present": "The present moment is asking for your attention. This remedy helps you arrive, fully, right here.",
  loneliness: "Connection starts with the way you hold yourself. This remedy gently opens that door.",
  oversensitive: "Feeling everything isn't a flaw — it just needs a boundary. This remedy helps you hold your own shape.",
  despondency: "Heavy days don't mean broken ones. This remedy meets you exactly where you are, and walks beside you.",
  overcare: "Caring for others starts with caring for yourself, too. This remedy softens the grip, gently.",
};


// Ready-mix Bach Flower Remedy bottles for frequent emotional episodes.
export const PRODUCTS = [
  {
    id: "sleep-well",
    name: "Sleep Well",
    tagline: "Disturbed sleep • Night worry",
    body:
      "For evenings that spiral into worry-loops and mornings that arrive unrested. Softens the mind so sleep can find you.",
    accent: "bg-[#E7D5A8]/60",
  },
  {
    id: "exam-ready",
    name: "Exam Ready",
    tagline: "Focus • Exam anxiety • Children & teens",
    body:
      "Gentle support for children and teens through exams — steady focus, less panic, and calmer study routines.",
    accent: "bg-[#F6F2EB]",
  },
  {
    id: "adhd-support",
    name: "ADHD Support",
    tagline: "Focus • Restlessness • Overthinking",
    body:
      "A thoughtful blend of Vervain, Impatiens, Clematis, Walnut, White Chestnut, Scleranthus, Chestnut Bud and Hornbeam — chosen to gently support common ADHD-associated patterns like hyperactivity, scattered focus, impulsivity, mood swings and mental restlessness. Not a cure — a companion.",
    accent: "bg-[#E7D5A8]/40",
    article: {
      intro:
        "This is written as an experience-cum-feedback of Bach Flower Remedies for the emotional challenges faced by individuals under the ADHD ambit. It is not medical advice and in no way tries to \"cure\" ADHD. Bach Flower Remedies are not a miracle fix, but I have found they can gently support many of the emotional patterns that go along with ADHD.",
      sections: [
      {
        remedies: [
          {
            name: "Vervain",
            text:
              "For those who are overly enthusiastic, to the point of being fanatical, which can lead to stress and burnout. People who need Vervain have an inability to rest — their enthusiasm consumes them. They are keyed up, unable to relax, overdo things and always \"on\". Children who need Vervain can often be hyperactive and difficult to put to bed at night.",
          },
          {
            name: "Impatiens",
            text:
              "For people who are impatient and need things to move quickly, otherwise they get irritable. They do everything quickly and find it difficult to wait for things to run their natural course. This remedy helps with patience and slowing down.",
          },
          {
            name: "Clematis",
            text:
              "For focus and attention. Their head is in the clouds — daydreaming, lost in thoughts, absent-minded, scatterbrained. This remedy is great for grounding and bringing the person back into the present.",
          },
          {
            name: "Walnut",
            text:
              "For those who are overly sensitive to outside influence and can easily be led astray by more dominant characters or the world around them. Very helpful during times of change or transition.",
          },
          {
            name: "White Chestnut",
            text:
              "The remedy for overthinking, constant mental arguments and unwanted thoughts. When the mind goes round like a hamster wheel, replaying conversations and repeating worries. This remedy helps bring a calm, controlled mind.",
          },
          {
            name: "Scleranthus",
            text:
              "Relates to indecision and uncertainty — swaying between possibilities. Often experiences mood swings and extreme variations in temperament. This remedy helps achieve a calm, determined mind and better balance.",
          },
          {
            name: "Chestnut Bud",
            text:
              "For those who repeat the same mistakes or patterns and struggle to learn from past experiences. Very helpful for special children (ADHD or autistic) with bed-wetting or difficulties in reading and learning activities.",
          },
          {
            name: "Hornbeam",
            text:
              "For mental weariness, lack of motivation to start a task, and procrastination. This remedy brings strength to start what initially seems an insurmountable challenge.",
          },
        ],
      },
    ],
      onion:
        "Bach Flower Remedies approach emotions using an \"onion layer\" method — addressing the emotions on the surface first, then gently going deeper to reach any stuck emotion. It is transformative in nature and enables healing from the root, in due course of time.",
      conclusion:
        "The list above generally helps in ADHD cases, but a personalised mix prepared after a consultation is always most suited. At Bloom & Heal we offer a free consultation to understand your emotions and pick the right remedies for your personalised bottle. I hold a divine intention and belief to act, heal and resolve — rather than succumb and regret. I have witnessed the effectiveness of these remedies.",
    },
  },
  {
    id: "autism",
    name: "Autism Support",
    tagline: "Sensory calm • Communication • Emotional balance",
    body:
      "How natural remedies can support individuals on the autism spectrum — and their caregivers — to manage emotional wellbeing and navigate the world with greater ease.",
    accent: "bg-[#F9EAE8]/70",
    article: {
      intro:
        "Bach Flower Remedies provide a gentle yet pillar-strong support to individuals on the autism spectrum, as well as to their family members or primary caregivers who may be overwhelmed dealing with challenging situations. This article explores how natural remedies help individuals with autism manage their emotional wellbeing and navigate their world with greater ease.",
      sections: [
      {
        remedies: [
          { name: "Holly", text: "For those who may struggle with anger outbursts or heightened sensitivity. Holly promotes a more harmonious and balanced emotional state." },
          { name: "Beech", text: "For tolerance and understanding. Beneficial for those who struggle with social interactions, have difficulty tolerating differences in others, or get easily annoyed by small things — including picky eating." },
          { name: "Rock Water", text: "For those who tend to be rigid and less flexible. Helps promote a more open-minded approach, helping to go with the flow rather than needing things a certain, unchanging way." },
          { name: "Rock Rose", text: "For those who may experience intense anxiety or emotional meltdowns. Provides a sense of courage and calm, helping to manage moments of acute distress." },
          { name: "Vine", text: "For those who tend to be domineering, inflexible or strong-willed. May assist in finding a balance between leadership and cooperation, leading to improved social dynamics." },
          { name: "Walnut", text: "Often called a \"link-breaker\" remedy — helps individuals adapt to changes and transitions, such as a change in school, environment or daily schedule, and offers protection from sensitivities like noise." },
          { name: "Chestnut Bud", text: "For those who often repeat the same mistakes or patterns and take a long time to learn from past experience. Encourages attentiveness and the ability to absorb new information — very helpful for reading and learning activities." },
          { name: "Water Violet", text: "For those who tend to be distant, private, reserved or aloof. For autistic individuals having difficulty connecting with others, this remedy facilitates openness and communication." },
          { name: "Clematis", text: "For focus and attention — daydreaming, lost in thoughts, scatterbrained, forgetful. Clematis helps anchor focus, ground them in reality and enhance engagement with the world around them." },
          { name: "White Chestnut", text: "For overthinking, persistent unwanted thoughts and mental chatter. May be supportive for individuals experiencing repetitive or intrusive thoughts, helping to quiet the mind and reduce anxiety." },
        ],
      },
    ],
      onion:
        "Bach flower remedies aim to approach the emotions in an onion layer method — addressing the emotions on the surface before digging deeper to reach any stuck emotion. It is transformative in nature and enables healing of deeper emotional patterns over time.",
      conclusion:
        "The above list of remedies generally helps individuals under the autistic spectrum or with similar emotional and behavioral challenges. However, a personalised mix prepared after a consultation is always most suited. At Bloom & Heal we offer a complimentary consultation to better understand the emotional challenges and pick the suitable remedies for your personalised mix bottle.",
      note:
        "This article is based on my personal experience and feedback regarding the use of Bach Flower Remedies as emotional support for individuals who experience challenges commonly associated with autism. It is not medical advice, diagnosis or treatment, and Bach Flower Remedies are not presented here as a cure for autism. Autism is a neurodevelopmental condition, and individuals should continue to seek appropriate guidance from qualified medical and mental-health professionals.",
    },
  },
  {
    id: "feeling-stuck",
    name: "Feeling Stuck",
    tagline: "Direction • Motivation • Clarity",
    body:
      "Feeling stuck can mean different things — confusion, procrastination, lack of self-trust, or simply feeling flat. Ten remedies that gently support the different faces of feeling stuck.",
    accent: "bg-[#F6F2EB]",
    article: {
      intro:
        "Feeling stuck could mean different things to different individuals — not knowing what to do, procrastination, lack of self-trust or willpower, or simply feeling flat. It could also be because of past events or fears. Keeping in mind that Bach flower remedies always help when feeling stuck, and based on my experience witnessing this, I am writing this article as a Bach flower therapist and counsellor.",
      sections: [
      {
        remedies: [
          { name: "Wild Oat", text: "For feeling lost or lacking direction in life. It doesn't suddenly provide a complete plan for your future, but helps you connect with what feels right for you — for when you don't know which direction to take." },
          { name: "Gorse", text: "For feeling stuck that comes from disappointment and loss of hope — a sense of \"what's the point?\" because change feels impossible after past failures. For when you have stopped believing that things can change." },
          { name: "Wild Rose", text: "For simply feeling flat, resigned, unmotivated and disconnected — accepting a situation as-is. For when you have simply accepted that this is how life is." },
          { name: "Hornbeam", text: "For feeling stuck when we can't get started — the \"Monday blues\", mental tiredness and procrastination. Helps make tasks feel more manageable and approach the day with fresh energy." },
          { name: "Honeysuckle", text: "For feeling stuck and unable to move forward when emotionally looking backwards, overly attached to the past. Helps us value our memories without becoming trapped in them." },
          { name: "Walnut", text: "For feeling stuck in the midst of transition or settling in after a change — job, location, or life-stage changes. Also for those stuck because of other people's opinions. A link-breaker that supports moving from one stage of life to another." },
          { name: "Larch", text: "For feeling stuck because of a lack of confidence in your abilities. Larch supports self-belief and the willingness to try — feeling that you deserve the opportunity to find out, and doing it anyway." },
          { name: "Scleranthus", text: "For feeling stuck due to indecision — swinging between possibilities, going forward and backwards internally. Scleranthus supports greater inner balance and decisiveness." },
          { name: "Clematis", text: "For feeling stuck because attention drifts from the present — lost or absent-minded, dreaming, disconnected. Clematis helps bring us back to the present so ideas can become something real." },
          { name: "White Chestnut", text: "For feeling stuck due to overthinking, constant mental arguments and unwanted thoughts — a mind that goes round like a hamster wheel. Helps calm the mind and let go of repetitive, negative thinking." },
        ],
      },
    ],
      onion:
        "Bach flower remedies aim to approach the emotions in an onion layer method — addressing the emotions on the surface before digging deeper to reach any stuck emotion. It is transformative in nature and enables healing of deeper emotional patterns over time.",
      conclusion:
        "The Bach flower remedies won't make decisions for you or change your circumstances. What they can do is support the emotions that may be keeping you in place, helping you feel clearer and more able to take your next step. A personalised mix prepared after a consultation is always most suited.",
      note:
        "This article is based on my personal experience and feedback regarding the use of Bach Flower Remedies as emotional support for individuals who experience the feeling of being stuck in life. Bach Flower Remedies are used as a complementary approach to support emotional wellbeing and do not claim to change or take decisions or actions for you.",
    },
  },
  {
    id: "caregivers",
    name: "Caregiver Support",
    tagline: "Moms • Caregivers • Hospitality",
    body:
      "For caregivers who give constantly to others — moms, family caregivers, and hospitality professionals — gentle support for the exhaustion, guilt and emotional strain that comes with caring for others.",
    accent: "bg-[#F9EAE8]/50",
    article: {
      intro:
        "For caregivers, who often deal with a wide range of emotions, these remedies can be a helpful support for managing stress, exhaustion, and emotional strain. I am writing this article as a Bach flower therapist to bring about an awareness of how Bach flower remedies can help with the emotional challenges faced by caregivers.",
      sections: [
      {
        heading: "Exhaustion & Burnout",
        note: "Constantly giving to others without taking time to recharge, leaving you physically and emotionally drained.",
        remedies: [
          { name: "Oak", text: "For people who don't stop even when exhausted. Helps calm down while balancing the need for such overwork." },
          { name: "Centaury", text: "For people who always put others first and can't say no. Supports setting boundaries and avoids reaching burnout level." },
        ],
      },
      {
        heading: "Overwhelm & Stress",
        note: "Juggling multiple responsibilities can leave you feeling stretched too thin and struggling to cope.",
        remedies: [
          { name: "Olive", text: "For when you are completely drained physically, mentally, and emotionally. Helps restore your energy." },
        ],
      },
      {
        heading: "Guilt & Self-Doubt",
        note: "Worrying whether you're doing enough, second-guessing your decisions, or feeling like you should be able to do more.",
        remedies: [
          { name: "Elm", text: "Helps in cases where one experiences overwhelm and self-doubt regarding responsibility." },
        ],
      },
      {
        heading: "Emotional Drain & Anxiety",
        note: "Taking on the emotions of those you care for, feeling their pain and stress as if it were your own.",
        remedies: [
          { name: "Red Chestnut", text: "Helps with over-worry and emotional drain." },
          { name: "Holly", text: "Helps to ease out anxiety." },
        ],
      },
      {
        heading: "Frustration & Resentment",
        note: "Feeling unappreciated or emotionally exhausted from constantly giving without receiving support in return.",
        remedies: [
          { name: "Cherry Plum", text: "Apt when one feels at the edge and experiences frustration — brings a sense of calmness." },
          { name: "Willow", text: "Helps shift resentment and the feeling that life is unfair, bringing acceptance." },
        ],
      },
      {
        heading: "Irritation & Fatigue",
        note: "Feeling irritated, intolerant or critical. Struggling to find the energy to keep going, even when others are relying on you.",
        remedies: [
          { name: "Beech", text: "Helps instill tolerance and understanding." },
          { name: "Hornbeam", text: "Helps the struggle to get started, or mental sluggishness." },
        ],
      },
    ],
      onion:
        "Bach flower remedies aim to approach the emotions in an onion layer method — addressing the emotions on the surface before digging deeper to reach any stuck emotion. It is transformative in nature and enables healing of deeper emotional patterns over time.",
      conclusion:
        "The Bach flower remedies won't change your circumstances or people around you. What they can do is support the emotions that may be keeping you in place, helping you feel clearer and more able to take your next step. A personalised mix prepared after a consultation is always most suited.",
      note:
        "This article is based on my personal experience and feedback regarding the use of Bach Flower Remedies as emotional support for individuals who are caregivers for family members, or who do it as a job to sustain their livelihood. Bach Flower Remedies are used as a complementary approach to support emotional wellbeing and do not claim to change your circumstances or the people around you.",
    },
  },
  {
    id: "anger-aggression",
    name: "Anger & Aggression",
    tagline: "Anger • Aggression • Emotional control",
    body:
      "Ten remedies that help calm impulsive, aggressive or explosive reactions and restore emotional equilibrium — a companion for those working through anger, resentment or a short fuse.",
    accent: "bg-[#F9EAE8]/60",
    article: {
      intro:
        "Bach Flower Remedies provide a natural solution to individuals who are struggling to maintain their sanity and emotional balance and tend to get aggressive, impulsive, abusive, rude, offensive, disrespectful and/or even violent. This article aims to elaborate how natural remedies would navigate such negative and extreme behavioral challenges into developing emotional wellbeing. Each case may differ in facts and reasons, so it is imperative to select remedies based on the individual's circumstances — such emotional patterns are generally deep-rooted, so remedies should be taken consistently and timely for lasting, sustainable balance.",
      sections: [
        {
          remedies: [
            { name: "Holly", text: "Addresses negative feelings of anger, aggression, jealousy, hatred, suspicion, envy and spite. Aims to bring openness, inner peace and warmth, promoting a more harmonious and balanced emotional state." },
            { name: "Willow", text: "Aids in letting go of resentment, bitterness, self-pity and a \"poor me\" attitude. Aims at promoting positivity and forgiveness for a more empowering mindset." },
            { name: "Beech", text: "Promotes tolerance and understanding. Helps reduce criticism and irritability, encouraging a more empathetic perspective — beneficial for those who get easily annoyed by small things." },
            { name: "Rock Water", text: "For the perfectionist, rigid and self-disciplined person who has high standards for self as well as others. Aims to let go of self-restraint and rigidity, promoting flexibility and open-mindedness." },
            { name: "Cherry Plum", text: "Addresses fear of loss of control, uncontrolled outbursts of rage, and fear that one may harm oneself or others. Brings calmness and composure." },
            { name: "Impatiens", text: "Addresses lack of patience and feelings of irritation, mental tension, frustration or restlessness caused by slow-paced people or things. Helps develop patience and a calmer, less rushed way of living." },
            { name: "Vine", text: "Addresses patterns of dominance, rigidity and forceful or dictatorial behaviour. Aims to develop the qualities of a wise, gentle guide who can inspire and lead others without resorting to force." },
            { name: "Centaury", text: "For people who get influenced by others and can't say no — weak-willed people-pleasers who can build up anger and frustration within. This remedy supports courage and self-determination." },
            { name: "Scleranthus", text: "For people who struggle making decisions and experience mood swings and extreme variations in temperament — indecisiveness that can lead to anger and lack of balance. Supports greater balance, poise and decisiveness." },
          ],
        },
      ],
      onion:
        "Bach flower remedies aim to approach the emotions in an onion layer method — addressing emotions on the surface before digging deeper to reach any stuck emotion. In cases of anger and aggression, we address the surface before the source, depending on the underlying cause and severity, using the appropriate remedy — the root is eventually addressed once we reach it.",
      conclusion:
        "The above list of Bach flower remedies generally helps in anger and aggression cases. However, a personalised mix prepared after a consultation is always most suited. At Bloom & Heal we offer a complimentary consultation to better understand the emotional challenges and pick the suitable remedies for your personalised mix bottle. I hold a deep intention and belief to act, heal and resolve, rather than succumb and regret. I have witnessed the effectiveness of these remedies.",
      note:
        "This article is based on my personal experience and feedback regarding the use of Bach Flower Remedies as emotional support for individuals who experience challenges commonly associated with anger and aggression. It is not medical advice, diagnosis or treatment, and individuals may continue to seek and take appropriate medications as required from a qualified medical professional. Bach Flower Remedies are used as a complementary approach to support emotional wellbeing.",
    },
  },
];

export const PRODUCT_PRICE_INR = 1500;

export const TIME_SLOTS = [
  "09:00 AM",
  "10:00 AM",
  "11:00 AM",
  "12:00 PM",
  "02:00 PM",
  "03:00 PM",
  "04:00 PM",
  "05:00 PM",
  "06:00 PM",
  "07:00 PM",
];

// Client testimonials — varied length and themes.
export const TESTIMONIALS = [
  {
    id: "r1",
    name: "Mother of a 6-year-old",
    role: "Mumbai • Homemaker",
    body:
      "Thank you so much, Gita, for making the Bach Flower Remedy mix for my daughter and for all the healing you put into it. It has made such a big difference to her. Her fears have reduced so much, and she's now happily going to school and eating her meals without any struggle. Seeing her so much more relaxed has been such a relief for us. Even my own Bach Flower Remedy mix has helped me a lot. I used to constantly worry about my loved ones, but those overwhelming emotions have eased so much. I feel much calmer and lighter now. Thank you from the bottom of my heart.",
    rating: 5,
  },
  {
    id: "r2",
    name: "Chartered Accountant",
    role: "Mumbai",
    body:
      "Gita Di, I'm so thankful for your EFT sessions and Bach Flower Remedies. In just three sessions and one bottle of remedies, I could actually feel a shift within myself. I feel much more confident, positive, and emotionally balanced. The situations that used to make me anxious or completely drain me don't affect me the way they used to. I handle them with a much calmer mind now. The best part is that even people around me have noticed the change and have told me how different I seem. I know this is an ongoing journey, and I'm still working on myself. But with your support, guidance, EFT, and Bach Flower Remedies, I truly feel that lasting change is possible. Thank you.",
    rating: 5,
  },
  {
    id: "r3",
    name: "Investment Manager & Chartered Accountant",
    role: "Mumbai",
    body:
      "I was dealing with a lot of anxiety and was constantly afraid of taking risks because of fear of failure. The pressure at work made me feel like I was never good enough, no matter how hard I worked. I found myself working endlessly, yet I couldn't focus properly or maintain any work-life balance. I was also unable to express myself, just to avoid conflicts and arguments. The Bach Flower Remedies helped me through some really difficult times. I began to feel calmer, less anxious, and more confident in handling challenges. Instead of being overwhelmed by fear, I could look at situations more rationally, understand the risks involved, make decisions, and move forward with a peaceful mind. Thank you.",
    rating: 5,
  },
  {
    id: "r4",
    name: "Parent of a teenage boy",
    role: "Mumbai",
    body:
      "Bach Flower Remedies have really helped my son with his aggression and low self-esteem. He used to be very clingy, strong-willed, and difficult to handle during emotional moments. We've seen a noticeable improvement, and he's much calmer now. We're continuing the remedies and are happy with the positive changes we've seen so far.",
    rating: 5,
  },
  {
    id: "t1",
    name: "Riya M. (30)",
    role: "Mumbai • New mother",
    body:
      "The first months after my baby were a quiet storm — exhausted, tearful, guilty for not feeling joyful enough. I tried to push through but it kept catching up with me. A friend gently nudged me towards Gita. The very first session felt different — there was no rush, no fixing, just listening. Over a few weeks of the personalised remedy and our conversations, the heaviness started to lift in small steady ways. I slept a little deeper, I cried a little less, I held my baby with more presence and less fear. Today, I look back at that phase with a lot of compassion for the woman I was, and a lot of gratitude for the woman who held space for me.",
    rating: 5,
  },
  {
    id: "t2",
    name: "Nisha K. (33)",
    role: "Bengaluru • Career change",
    body:
      "I quit a 9-year corporate role and was paralysed by self-doubt. Two months of Bach Flower sessions moved me from fear to clarity. I finally made the switch.",
    rating: 5,
  },
  {
    id: "t3",
    name: "Anjali D. (38)",
    role: "Mumbai • Parent",
    body:
      "My daughter had developed real anxiety around school — stomach aches every morning, tears at the gate, and a kind of dread I couldn't reason her out of. We tried pep talks, rewards, even a child counsellor briefly, but nothing seemed to stick. A neighbour mentioned Gita, and I went in mostly out of desperation. Gita worked with my daughter softly, almost like a friend, and prepared a children's flower remedy along with a separate one for me — because, as she gently pointed out, my own anxiety was passing onto hers. Within about six weeks there was a visible shift. My daughter walks into class on her own now, and I have learnt to breathe before drop-off. It has changed our mornings completely.",
    rating: 5,
  },
  {
    id: "t4",
    name: "Priya S. (34)",
    role: "Pune • Parent",
    body:
      "Drop-offs at preschool were heartbreaking — my son would cling on for dear life. Gita worked with both of us. In six weeks, the separation anxiety eased.",
    rating: 4,
  },
  {
    id: "t5",
    name: "Meera J. (36)",
    role: "Gurgaon • Working professional",
    body:
      "Constant office pressure was making me snap at home and barely sleep. The mix of personal counselling and a tailored Bach Flower bottle softened the edge. I still have demanding days — I just don't carry them home anymore.",
    rating: 5,
  },
  {
    id: "t6",
    name: "Sneha R. (42)",
    role: "Mumbai • Bereavement",
    body:
      "After losing my mother, I went numb. I didn't cry, I didn't break down, I didn't even feel angry — I just functioned, almost robotically, and everyone around me thought I was 'doing well'. Inside, I knew something was very, very off. A cousin recommended Gita, and I remember sitting in our first session not knowing what to say. She didn't push. She just let me sit in the silence and offered words only when it felt right. The first remedy she made for me felt like a slow thaw. Weeks turned into months, and the grief that had been frozen began to move — sometimes in tears, sometimes in unexpected laughter, sometimes in old memories I had refused to revisit. I will always be grateful for the patience and tenderness she held me with through that time.",
    rating: 5,
  },
  {
    id: "t7",
    name: "Tanvi A. (37)",
    role: "Relocated to Dubai • Homemaker",
    body:
      "Moving country with two kids unravelled me more than I expected. The online sessions were a lifeline — the remedies felt like a quiet anchor in a new city.",
    rating: 4,
  },
  {
    id: "t8",
    name: "Aarti P. (45)",
    role: "Mumbai • Childhood trauma",
    body:
      "I carried a lot from my childhood — things I had never spoken about, not even to my closest friends. I had built a perfectly functioning life around it, but every few months something would tip me over and I would go quiet for days. I came to Gita not really believing anything could shift such old patterns. What surprised me was how unhurried the work was. She never asked me to relive anything I wasn't ready to share. She used Bach Flower Remedies, gentle counselling, and at points EFT, depending on what the moment needed. Slowly, layer by layer, the grip on those old memories loosened. I am not 'fixed' — I don't think anyone ever is — but I no longer feel hijacked by my past. That, for me, is a kind of freedom I had stopped believing in.",
    rating: 5,
  },
  {
    id: "t9",
    name: "Devika N. (28)",
    role: "Pune • Living with OCD",
    body:
      "My OCD had quietly taken over my mornings — the checking, the counting, the dread if a routine was broken. Sessions with Gita, alongside my regular medical care, helped me work on the underlying anxiety. It is much less loud now.",
    rating: 4,
  },
  {
    id: "t10",
    name: "Karan V. (39)",
    role: "Delhi • Entrepreneur",
    body:
      "Two months of sessions and a personalised remedy made me far more patient at home. I'd recommend Gita to any founder feeling this kind of strain.",
    rating: 5,
  },
  {
    id: "t11",
    name: "Shalini B. (41)",
    role: "Mumbai • After divorce",
    body:
      "My divorce was an ugly one, and it left me far more bruised than I thought I would be. What made it harder was everything around it — neighbours whispering, relatives passing judgement, a few old friends quietly stepping away because divorce, in our circles, is still spoken about in hushed tones. On top of that, I had to change my job in the middle of it all and the new role wasn't easy. There were months when I genuinely lost faith in life — in people, in fairness, in myself. I came to Gita on a friend's insistence, more out of exhaustion than belief. She listened without flinching at any of it. The Bach Flower Remedies, week after week, did something I cannot fully explain in words — the bitterness began to soften, the heaviness lifted in layers, and slowly the part of me that used to believe in goodness came back. Today I am rebuilding my life on my own terms. I laugh more easily, I trust again, and I have embraced happiness without apology. I will always be grateful for the steady hand Gita extended during my darkest stretch.",
    rating: 5,
  },
];

// Practitioner bio — Gita Mansukhani, Founder of Bloom & Heal.
export const PRACTITIONER = {
  name: "Gita Mansukhani",
  title: "Certified Bach Flower Therapist",
  role: "Founder of Bloom & Heal",
  photo:
    "https://customer-assets.emergentagent.com/job_dropdown-portal/artifacts/zy65vn76_WhatsApp%20Image%202026-06-13%20at%2020.56.46.jpeg",
  credentials: [
    "Chartered Accountant • 10+ years in finance",
    "Certified Bach Flower Therapist",
    "Certified EFT (Emotional Freedom Technique) Practitioner",
    "Trained in Counselling",
    "Founder of Bloom & Heal",
  ],
  bio: [
    "Gita holds a Chartered Accountant degree and has spent more than a decade of successful career in the finance industry — a career that gave her precision, discipline and a quiet front-row seat to how stress and unspoken emotion shape everyday life.",
    "Over time, she found herself drawn to alternative healing modalities — Bach Flower Remedies, EFT (Emotional Freedom Technique), Personal Counselling, Reiki and more — and slowly, what began as personal curiosity became a deeper calling. She trained, practised and is certified across many modalities, with her specialisation in Bach Flower therapy, EFT and Personal Counselling — the modalities she felt most at home with.",
    "As the founder of <i>Bloom & Heal</i>, Gita has supported many people — children, teenagers, working professionals, parents and elders — through their emotional challenges. Her sessions are calm, judgement-free and shaped around the person, never a protocol; her work weaves together attentive counselling and personalised flower-essence blends, prepared only after deep listening.",
  ],
};

